import {Box, Loader, Text, TextInput} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import type {FC, FormEvent} from 'react';
import {MdMarkEmailUnread} from 'react-icons/md';
import {
  INQUIRY_FOREIGN_UNIV,
  STUDENT_CARD,
} from '../../../config/constants/routes';
import {AcEmailResendButton} from '../AcEmailResendButton';
import {CommonLink} from '../CommonLink';

type PendingProps = {
  pendingAcEmail: string;
  onResend: () => void;
  isResending: boolean;
};

type VerifiedProps = {
  acEmail: string;
};

type InputProps = {
  inputValue: string;
  errorMessage?: string;
  isSubmitting: boolean;
  onChangeInput: (value: string) => void;
  onSubmit: () => void;
};

type StateProps = {
  isProfileLoaded: boolean;
  isLoading: boolean;
  isVerifiedByStudentCard: boolean;
};

// どの状態を描画するかは StateProps と acEmail/pendingAcEmail で決まるため、全状態のpropsを受け取る
type Props = PendingProps & VerifiedProps & InputProps & StateProps;

const PendingVerification: FC<PendingProps> = ({
  pendingAcEmail,
  onResend,
  isResending,
}) => (
  <Box>
    <Box className="rounded-4 bg-background-4 p-16">
      <Box className="flex items-start gap-8">
        <MdMarkEmailUnread size={20} className="mt-4 shrink-0 text-accent" />
        <Box>
          <Text className="font-bold text-black-1">
            認証メールを送信しました
          </Text>
          <Text className="mt-4 break-all font-bold text-black-1">
            {pendingAcEmail}
          </Text>
        </Box>
      </Box>
    </Box>

    <Text className="mt-16 leading text-black-1">
      メールに記載されているURLにアクセスすると認証が完了します。
    </Text>
    <Text className="mt-4 leading text-black-1">
      有効期限は<span className="font-bold">48時間</span>です。
    </Text>

    <Box className="mt-20 rounded-4 bg-white p-16">
      <Text className="text-sm leading text-black-2">
        メールが届かない場合は、迷惑メールフォルダをご確認のうえ、下のボタンから再送してください。
      </Text>
      <Box className="mt-16 flex justify-center">
        <AcEmailResendButton onResend={onResend} isResending={isResending} />
      </Box>
    </Box>
  </Box>
);

const DisabledAcEmailField: FC<{
  value?: string;
  placeholder?: string;
  description: string;
}> = ({value, placeholder, description}) => (
  <Box>
    <TextInput
      label="大学メールアドレス"
      placeholder={placeholder}
      size="md"
      value={value}
      disabled={true}
      readOnly={true}
    />
    <Text className="mt-8 text-sm leading text-black-2">{description}</Text>
    <Text className="mt-4 text-sm leading text-black-2">
      各種案内などはすべてログイン用メールアドレス宛に送信されます。
    </Text>
  </Box>
);

const VerifiedAcEmail: FC<VerifiedProps> = ({acEmail}) => (
  <DisabledAcEmailField
    value={acEmail}
    description="認証済みの大学メールアドレスは変更できません。"
  />
);

const StudentCardVerified: FC = () => (
  <Box>
    <Box className="rounded-4 bg-background-1 p-16">
      <Text className="font-bold text-black-1">
        学生証による在籍確認が完了しています。
      </Text>
    </Box>
    <Box className="mt-16">
      <DisabledAcEmailField
        placeholder="大学メールアドレス"
        description="在籍確認が完了しているため、大学メールアドレスは登録できません。"
      />
    </Box>
  </Box>
);

const AcEmailInputForm: FC<InputProps> = ({
  inputValue,
  errorMessage,
  isSubmitting,
  onChangeInput,
  onSubmit,
}) => (
  <Box
    component="form"
    onSubmit={(event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit();
    }}
  >
    <TextInput
      label="大学メールアドレス"
      placeholder="gaishishukatsu@university.ac.jp"
      type="email"
      size="md"
      value={inputValue}
      error={errorMessage}
      onChange={(event) => {
        onChangeInput(event.currentTarget.value);
      }}
    />
    <Text className="mt-8 text-sm leading text-black-2">
      入力されたアドレス宛に認証用のURLを記載したメールを送信します。
    </Text>

    <Box className="mt-20">
      <BasicButton
        isPrimary={true}
        isLarge={true}
        type="submit"
        isDisabled={isSubmitting || inputValue === ''}
      >
        認証メールを送信する
      </BasicButton>
    </Box>

    <Box className="mt-24 pt-16">
      <Text className="text-base leading text-black-2">
        大学メールアドレスが使えない方は
        <CommonLink href={STUDENT_CARD} className="text-link underline">
          学生証で登録
        </CommonLink>
        してください。
      </Text>
      <Text className="mt-4 text-base leading text-black-2">
        海外の大学に在学中の方は
        <CommonLink href={INQUIRY_FOREIGN_UNIV} className="text-link underline">
          こちら
        </CommonLink>
        からお問い合わせください。
      </Text>
    </Box>
  </Box>
);

export const AcEmailVerificationFormPresenter: FC<Props> = ({
  acEmail,
  pendingAcEmail,
  isProfileLoaded,
  isLoading,
  isVerifiedByStudentCard,
  onResend,
  isResending,
  ...inputProps
}) => {
  if (isLoading) {
    return (
      <Box className="flex justify-center py-32">
        <Loader />
      </Box>
    );
  }

  if (!isProfileLoaded) {
    return (
      <Box className="rounded-4 bg-background-1 p-16">
        <Text className="text-sm leading text-black-2">
          プロフィール情報を取得できませんでした。時間をおいて再度お試しください。
        </Text>
      </Box>
    );
  }

  if (pendingAcEmail) {
    return (
      <PendingVerification
        pendingAcEmail={pendingAcEmail}
        onResend={onResend}
        isResending={isResending}
      />
    );
  }

  if (acEmail) {
    return <VerifiedAcEmail acEmail={acEmail} />;
  }

  if (isVerifiedByStudentCard) {
    return <StudentCardVerified />;
  }

  return <AcEmailInputForm {...inputProps} />;
};
